import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Dropdown, Empty, Typography } from "antd";
import {
  BellOutlined,
  FileDoneOutlined,
  MessageOutlined,
  RocketOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { RootReducerProps } from "../../../store/reducers";
import * as InAppNotificationActions from "../../../store/actions/inAppNotificationActions";
import AppNotificationsService from "../../../services/AppNotifications/appNotifications.service";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import {
  InAppNotification,
  InAppNotificationKind,
} from "../../../types/inAppNotification";
import { AppRoutes } from "../../../routes/routeConstants/appRoutes";
import { warmNotificationAudio } from "../../../shared/utils/playNotificationChime";
import "./notificationBell.scss";

const { Text } = Typography;

interface NotificationBellProps {
  userRole?: UserRoleEnum;
  inAppNotifications: {
    items: InAppNotification[];
    unreadCount: number;
  };
  mergeInAppNotifications: (
    notifications: InAppNotification[],
    unreadCount: number
  ) => void;
  pushInAppNotification: (n: InAppNotification) => void;
  markInAppNotificationRead: (id: number) => void;
  markAllInAppNotificationsRead: () => void;
}

function kindIcon(kind: InAppNotificationKind) {
  switch (kind) {
    case "chat_message":
      return <MessageOutlined />;
    case "case_reported":
      return <FileDoneOutlined />;
    case "case_assigned":
      return <UserAddOutlined />;
    case "release":
      return <RocketOutlined />;
    default:
      return <BellOutlined />;
  }
}

function kindClass(kind: InAppNotificationKind): string {
  switch (kind) {
    case "chat_message":
      return "notification-bell__icon-wrap--chat";
    case "case_reported":
      return "notification-bell__icon-wrap--reported";
    case "case_assigned":
      return "notification-bell__icon-wrap--assigned";
    case "release":
      return "notification-bell__icon-wrap--release";
    default:
      return "notification-bell__icon-wrap--chat";
  }
}

/** Collapsed row: short line (chat uses preview so we don’t repeat the generic body). */
function messageBodyOrPreview(n: InAppNotification): string {
  if (n.kind === "chat_message") {
    const preview = n.payload.message_preview?.trim();
    if (preview) return preview;
    return n.payload.message_text?.trim() || n.body?.trim() || "";
  }
  return n.body?.trim() || n.payload.message_preview?.trim() || "";
}

/** Expanded “View”: full text for chat (body is “New chat message — …”, not the message). */
function messageDisplayText(n: InAppNotification): string {
  if (n.kind === "chat_message") {
    const full = n.payload.message_text?.trim();
    if (full) return full;
    return (
      n.payload.message_preview?.trim() ||
      n.body?.trim() ||
      n.title?.trim() ||
      ""
    );
  }
  const b = n.body?.trim() || n.payload.message_preview?.trim() || "";
  if (b) return b;
  return n.title?.trim() || "";
}

function showViewForNotification(n: InAppNotification): boolean {
  if (n.kind === "release") {
    return !!(
      n.body?.trim() ||
      n.payload.patient_name ||
      n.payload.case_date ||
      n.payload.version
    );
  }
  return messageDisplayText(n).length > 0;
}

function NotificationBell({
  userRole,
  inAppNotifications,
  mergeInAppNotifications,
  markInAppNotificationRead,
  markAllInAppNotificationsRead,
}: NotificationBellProps) {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const refresh = useCallback(() => {
    if (!userRole) return;
    AppNotificationsService.fetchList(
      userRole,
      (data) => {
        mergeInAppNotifications(data.notifications, data.unread_count);
      },
      () => {}
    );
  }, [userRole, mergeInAppNotifications]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const markReadIfNeeded = useCallback(
    (n: InAppNotification) => {
      if (!userRole || n.read_at) return;
      AppNotificationsService.markRead(userRole, n.id, (updated) => {
        markInAppNotificationRead(updated.id);
      });
    },
    [userRole, markInAppNotificationRead]
  );

  /** Only for `release`: expands detail, then opens in-app release notes (HTML from API). */
  const handleReleaseOpen = useCallback(
    (n: InAppNotification, e: React.MouseEvent) => {
      e.stopPropagation();
      if (n.kind !== "release") return;
      markReadIfNeeded(n);
      setExpandedId(n.id);
      const v = n.payload.version || "";
      window.setTimeout(() => {
        setOpen(false);
        history.push(
          `${AppRoutes.RELEASE_NOTES}${
            v ? `?v=${encodeURIComponent(v)}` : ""
          }`
        );
      }, 450);
    },
    [history, markReadIfNeeded]
  );

  const handleViewClick = useCallback(
    (n: InAppNotification, e: React.MouseEvent) => {
      e.stopPropagation();
      markReadIfNeeded(n);
      setExpandedId((prev) => (prev === n.id ? null : n.id));
    },
    [markReadIfNeeded]
  );

  const markAll = useCallback(() => {
    if (!userRole) return;
    AppNotificationsService.markAllRead(userRole, () => {
      markAllInAppNotificationsRead();
    });
  }, [userRole, markAllInAppNotificationsRead]);

  const items = inAppNotifications.items;
  const unread = inAppNotifications.unreadCount;

  const dropdown = useMemo(
    () => (
      <div className="notification-bell__panel">
        <div className="notification-bell__header">
          <span>Notifications</span>
          {unread > 0 ? (
            <Button type="link" size="small" onClick={markAll}>
              Mark all read
            </Button>
          ) : null}
        </div>
        <div className="notification-bell__list">
          {items.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="You're all caught up"
              style={{ padding: "24px 16px" }}
            />
          ) : (
            items.map((n) => {
              const isUnread = !n.read_at;
              const expanded = expandedId === n.id;
              const bodyOrPreview = messageBodyOrPreview(n);
              const displayText = messageDisplayText(n);
              const showView = showViewForNotification(n);
              const isRelease = n.kind === "release";

              return (
                <div
                  key={n.id}
                  className={`notification-bell__row ${
                    isUnread ? "notification-bell__row--unread" : ""
                  }`}
                >
                  <div
                    className={`notification-bell__icon-wrap ${kindClass(
                      n.kind
                    )}`}
                  >
                    {kindIcon(n.kind)}
                  </div>
                  <div className="notification-bell__body">
                    <div className="notification-bell__title">{n.title}</div>
                    {!expanded && bodyOrPreview ? (
                      <div className="notification-bell__subtitle">
                        {bodyOrPreview}
                      </div>
                    ) : null}
                    <div className="notification-bell__meta">
                      {moment(n.created_at).fromNow()}
                      {n.payload.case_label ? (
                        <span> · {n.payload.case_label}</span>
                      ) : null}
                    </div>
                    <div className="notification-bell__inline-actions">
                      {showView ? (
                        <button
                          type="button"
                          className="notification-bell__text-link"
                          onClick={(e) => handleViewClick(n, e)}
                        >
                          {expanded ? "Hide" : "View"}
                        </button>
                      ) : null}
                      {isRelease ? (
                        <>
                          {showView ? (
                            <span className="notification-bell__inline-sep">
                              ·
                            </span>
                          ) : null}
                          <button
                            type="button"
                            className="notification-bell__text-link"
                            onClick={(e) => handleReleaseOpen(n, e)}
                          >
                            Open
                          </button>
                        </>
                      ) : null}
                    </div>
                    {expanded && isRelease ? (
                      <div className="notification-bell__inline-detail notification-bell__inline-detail--release">
                        {n.body?.trim() ? (
                          <div className="notification-bell__release-block">
                            <div className="notification-bell__release-label">
                              Message
                            </div>
                            <Text className="notification-bell__inline-detail-text">
                              {n.body.trim()}
                            </Text>
                          </div>
                        ) : null}
                        {n.payload.patient_name ? (
                          <div className="notification-bell__release-block">
                            <div className="notification-bell__release-label">
                              Patient
                            </div>
                            <Text>{n.payload.patient_name}</Text>
                          </div>
                        ) : null}
                        {n.payload.case_date ? (
                          <div className="notification-bell__release-block">
                            <div className="notification-bell__release-label">
                              Case date
                            </div>
                            <Text>
                              {moment(n.payload.case_date).isValid()
                                ? moment(n.payload.case_date).format(
                                    "MMM D, YYYY"
                                  )
                                : n.payload.case_date}
                            </Text>
                          </div>
                        ) : null}
                        {n.payload.version ? (
                          <div className="notification-bell__release-block">
                            <div className="notification-bell__release-label">
                              Version
                            </div>
                            <Text>{n.payload.version}</Text>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {expanded && !isRelease && displayText ? (
                      <div className="notification-bell__inline-detail">
                        <Text className="notification-bell__inline-detail-text">
                          {displayText}
                        </Text>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    ),
    [
      items,
      unread,
      markAll,
      expandedId,
      handleViewClick,
      handleReleaseOpen,
    ]
  );

  if (!userRole) {
    return null;
  }

  return (
    <Dropdown
      overlay={dropdown}
      trigger={["click"]}
      visible={open}
      onVisibleChange={(v) => {
        setOpen(v);
        if (v) refresh();
        if (!v) setExpandedId(null);
      }}
      placement="bottomRight"
      overlayStyle={{ zIndex: 1050 }}
    >
      <span className="notification-bell">
        <Badge count={unread > 0 ? unread : 0} size="small" offset={[-2, 2]}>
          <button
            type="button"
            className="notification-bell__trigger"
            aria-label="Notifications"
            onPointerDown={warmNotificationAudio}
          >
            <BellOutlined style={{ fontSize: 18, color: "#1847E9" }} />
          </button>
        </Badge>
      </span>
    </Dropdown>
  );
}

const mapState = (state: RootReducerProps) => ({
  userRole: state.auth.userRole,
  inAppNotifications: state.inAppNotifications,
});

const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      ...InAppNotificationActions,
    },
    dispatch
  );

export default connect(mapState, mapDispatch)(NotificationBell);
